import React from 'react';
import styles from './style.module.scss';
import { Button, PageHeader } from 'components';
import { DotChartOutlined } from '@ant-design/icons';
import { Col, Table, Row, Typography } from 'antd';
import { ExcelIcon } from 'icons';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useInterviews from '../useInterviews';
import { ResponsivePie } from '@nivo/pie';
import moment from 'moment';

const getTspanGroups = (value, maxLineLength, maxLines = 2) => {
  const words = value.split(' ');
  //reduces the words into lines of maxLineLength
  const assembleLines = words.reduce(
    (acc, word) => {
      //if the current line isn't empty and the word + current line is larger than the allowed line size, create a new line and update current line
      if ((word + acc.currLine).length > maxLineLength && acc.currLine !== '') {
        return {
          lines: acc.lines.concat([acc.currLine]),
          currLine: word,
        };
      }
      //otherwise add the word to the current line
      return Object.assign(Object.assign({}, acc), {
        currLine: acc.currLine + ' ' + word,
      });
    },
    { lines: [], currLine: '' },
  );
  //add the ending state of current line (the last line) to lines
  const allLines = assembleLines.lines.concat([assembleLines.currLine]);
  //for now, only take first 2 lines due to tick spacing and possible overflow
  const lines = allLines.slice(0, maxLines);
  let children = [];
  let dy = 0;
  lines.forEach((lineText, i) => {
    children.push(
      React.createElement(
        'tspan',
        { x: 0, dy: dy, key: i },
        // if on the second line, and that line's length is within 3 of the max length, add ellipsis
        1 === i && allLines.length > 2
          ? lineText.slice(0, maxLineLength - 3) + '...'
          : lineText,
      ),
    );
    //increment dy to render next line text below
    dy += 15;
  });
  return children;
};

const AnswersPrint = () => {
  const { answers, handleDownloadExcelSingle, loading } = useInterviews(
    false,
    false,
    true,
  );
  const { t } = useTranslation();
  let [searchParams] = useSearchParams();
  localStorage.setItem('auth_token', searchParams.get('token'));
  return (
    <>
      <PageHeader title={t('SONDAGGIO A RISPOSTA CHIUSA')}></PageHeader>
      <div className={styles.container}>
        <Row gutter={16}>
          <Col span={24}>
            <Typography.Title className={styles.title} level={4}>
              {answers?.interview?.shopping_center}
            </Typography.Title>
            <Typography.Title level={4} className={styles.title}>
              {answers?.interview?.title}
            </Typography.Title>
            <Typography.Title level={4} className={styles.title}>
              {moment(answers?.interview?.created_at).format('DD MMMM YYYY')}
            </Typography.Title>
          </Col>
          <Col span={24}>
            <Typography.Title level={4}>
              {t('Clienti che hanno risposto')}: {answers.answer_count}
            </Typography.Title>
          </Col>
          {answers?.interview_questions?.map((question, index) => {
            console.log('question=', question);
            const data = question.chart.map((i) => ({
              id: i[0],
              label: i[0],
              value: i[1],
            }));

            const addClass = index !== 0;

            const columns = [
              {
                title: question.title,
                children: [
                  {
                    title: '',
                    dataIndex: 'label',
                    key: 'label',
                  },
                  {
                    title: '',
                    dataIndex: 'value',
                    key: 'value',
                  },
                ],
              },
            ];

            return (
              <Col
                className={
                  (addClass ? 'chartColl' : '') + ' ' + styles.chartTable
                }
                span={24}
                key={question.id}
              >
                <Row gutter={16}>
                  <Col span={24} key={index}>
                    <Typography.Title level={4}>
                      {question.title}
                    </Typography.Title>
                  </Col>
                  <Col span={12} className={styles.chart}>
                    <ResponsivePie
                      arcLinkLabelsStraightLength={17}
                      arcLinkLabelsTextOffset={1}
                      data={data}
                      margin={{ top: 40, right: 80, bottom: 150, left: 80 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={{ scheme: 'category10' }}
                      borderWidth={1}
                      arcLinkLabel={(d) => {
                        console.log('d=', d);
                        return (
                          <React.Fragment>
                            {getTspanGroups(d.data.label, 40, 5)}
                          </React.Fragment>
                        );
                      }}
                      borderColor={{
                        from: 'color',
                        modifiers: [['darker', 0.2]],
                      }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor='#333333'
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{
                        from: 'color',
                        modifiers: [['darker', 2]],
                      }}
                      defs={[
                        {
                          id: 'dots',
                          type: 'patternDots',
                          background: 'inherit',
                          color: 'rgba(255, 255, 255, 0.3)',
                          size: 4,
                          padding: 1,
                          stagger: true,
                        },
                        {
                          id: 'lines',
                          type: 'patternLines',
                          background: 'inherit',
                          color: 'rgba(255, 255, 255, 0.3)',
                          rotation: -45,
                          lineWidth: 6,
                          spacing: 10,
                        },
                      ]}
                      fill={[
                        {
                          match: {
                            id: 'ruby',
                          },
                          id: 'dots',
                        },
                        {
                          match: {
                            id: 'c',
                          },
                          id: 'dots',
                        },
                        {
                          match: {
                            id: 'go',
                          },
                          id: 'dots',
                        },
                        {
                          match: {
                            id: 'python',
                          },
                          id: 'dots',
                        },
                        {
                          match: {
                            id: 'scala',
                          },
                          id: 'lines',
                        },
                        {
                          match: {
                            id: 'lisp',
                          },
                          id: 'lines',
                        },
                        {
                          match: {
                            id: 'elixir',
                          },
                          id: 'lines',
                        },
                        {
                          match: {
                            id: 'javascript',
                          },
                          id: 'lines',
                        },
                      ]}
                      legends={[
                        {
                          anchor: 'bottom',
                          direction: 'column',
                          justify: false,
                          translateX: 0,
                          translateY: 150,
                          itemsSpacing: 0,
                          itemWidth: 180,
                          itemHeight: 18,
                          itemTextColor: '#999',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 18,
                          symbolShape: 'circle',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemTextColor: '#000',
                              },
                            },
                          ],
                        },
                      ]}
                    />
                  </Col>
                  <Col span={24}>
                    <Table
                      pagination={{
                        pageSize: 1000,
                        position: ['bottomCenter'],
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                      }}
                      dataSource={data}
                      bordered
                      columns={columns}
                    />
                  </Col>
                  <Col className={index === 0 ? styles.typography_title1 : styles.typography_title} span={24} key={index}>
                    <Typography.Title level={4} className={styles.title}>
                      {answers?.interview?.title}
                    </Typography.Title>
                  </Col>
                </Row>
              </Col>
            );
          })}
        </Row>
      </div>
    </>
  );
};

export default AnswersPrint;
